'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { activitiesGetWork, viduExploreDetailPro } from '@/app/apis';
import { useToast } from '@/app/components/ui/use-toast';
import type { ViduV1ExploreDetailProReply } from '@/app/types';
import {
  convertActivityWorkToExploreDetail,
  type FeedDetailType,
  findListItemById,
  getDetailId,
  getListItemForUpdate,
  getMetricsReportData,
  getTraceIdFromListItem,
  reportEvent,
  type ThumbnailListItem,
} from '@/app/utils';
import { updatePageMetadata } from '@/app/utils/client';
import { useLocale, usePathname } from '@/i18n/routing';
import { useFetch } from './use-fetch';
import { useMediaMetrics } from './use-media-metrics';

interface UseFeedDetailNavigationOptions {
  /** 初始详情数据 */
  initialData?: ViduV1ExploreDetailProReply;
  /** 初始 ID（用于首次加载） */
  initialId?: string;
  /** 列表数据（用于切换时的乐观渲染） */
  list?: ThumbnailListItem[];
  /** Feed 详情类型，默认为社区 */
  feedType?: FeedDetailType;
}

export const useFeedDetailNavigation = ({
  initialData,
  initialId,
  list = [],
  feedType = 'community',
}: UseFeedDetailNavigationOptions = {}) => {
  const { toast } = useToast();
  const locale = useLocale();
  const pathname = usePathname();
  const [currentData, setCurrentData] = useState<ViduV1ExploreDetailProReply | undefined>(initialData);
  // 记录上次上报的 ID，避免重复上报
  const lastReportedIdRef = useRef<string | undefined>(undefined);
  // 存储当前数据的 ref，用于在切换时获取准确的数据（确保初始值也被设置）
  const currentDataRef = useRef<ViduV1ExploreDetailProReply | undefined>(currentData);
  // 存储当前的 traceId（从列表数据中获取）
  const currentTraceIdRef = useRef<string>('');

  // 同步 currentData 到 ref
  useEffect(() => {
    currentDataRef.current = currentData;
  }, [currentData]);

  // 初始化时从列表中查找 traceId
  useEffect(() => {
    if (currentData && !currentTraceIdRef.current && list.length > 0) {
      const currentId = getDetailId(currentData);
      if (currentId) {
        const listItem = findListItemById(list, currentId);
        if (listItem) {
          currentTraceIdRef.current = getTraceIdFromListItem(listItem);
        }
      }
    }
  }, [currentData, list]);

  // 初始加载社区详情（用于 Modal 模式）
  const { loading: communityLoading } = useFetch(() => viduExploreDetailPro({ path: { id: initialId || '' } }), {
    ready: !!initialId && !initialData && feedType === 'community',
    onSuccess: ({ data }) => {
      if (data) {
        setCurrentData(data);
      }
    },
  });

  // 初始加载活动详情（用于 Modal 模式）
  const { loading: activityLoading } = useFetch(() => activitiesGetWork({ path: { id: initialId || '' } }), {
    ready: !!initialId && !initialData && feedType === 'activity',
    onSuccess: ({ data }) => {
      if (data) {
        const converted = convertActivityWorkToExploreDetail(data);
        if (converted) {
          setCurrentData(converted);
        }
      }
    },
  });

  const initialLoading = communityLoading || activityLoading;

  // 埋点上报 callback（使用 useCallback 配合 ref 确保引用稳定）
  const handleMetricsReport = useCallback((detailStayTime: number, completeTimes: number) => {
    const data = currentDataRef.current;
    if (!data) return;

    const reportData = getMetricsReportData(data);
    if (!reportData) return;

    const { eventName, eventIdKey, id } = reportData;

    reportEvent(eventName, {
      [eventIdKey]: id,
      complete_times: completeTimes,
      detail_staytime: detailStayTime,
      interaction_position: pathname,
      interaction_type: 'view_detail',
      trace_id: currentTraceIdRef.current,
      content_type: 'video',
    });
  }, []);

  const { handleEnter, handleLeave } = useMediaMetrics({
    callback: handleMetricsReport,
  });

  // 处理埋点上报：只在 ID 真正变化时才上报，避免乐观渲染导致的重复上报
  useEffect(() => {
    if (currentData) {
      const currentId = getDetailId(currentData);
      // 只有当 ID 变化时才调用 handleEnter（包括初始进入和切换）
      if (currentId && currentId !== lastReportedIdRef.current) {
        lastReportedIdRef.current = currentId;
        handleEnter();
      }
    }
  }, [currentData, handleEnter]);

  const { run: apiGetCommunityDetail, loading: communityDetailLoading } = useFetch(
    (id: string) => viduExploreDetailPro({ path: { id } }),
    {
      manual: true,
      onSuccess: ({ data, error }, [id]) => {
        if (error) {
          toast({ type: 'error', message: error.message });
          return;
        }
        if (data) {
          setCurrentData(data);
          updatePageMetadata(id, data, locale, feedType);
        }
      },
    },
  );

  const { run: apiGetActivityDetail, loading: activityDetailLoading } = useFetch(
    (id: string) => activitiesGetWork({ path: { id } }),
    {
      manual: true,
      onSuccess: ({ data, error }, [id]) => {
        if (error) {
          toast({ type: 'error', message: error.message });
          return;
        }
        if (data) {
          const converted = convertActivityWorkToExploreDetail(data);
          if (converted) {
            setCurrentData(converted);
            updatePageMetadata(id, converted, locale, feedType);
          }
        }
      },
    },
  );

  const detailLoading = communityDetailLoading || activityDetailLoading;

  // 处理缩略图点击（乐观渲染）
  const handleThumbnailClick = useCallback(
    (id: string, traceId?: string, index?: number) => {
      const currentId = currentData ? getDetailId(currentData) : '';
      if (id === currentId) return;

      // 先上报离开上一项
      handleLeave();

      // 获取列表项数据用于乐观渲染
      const updateData = getListItemForUpdate(list, id, index);

      if (updateData) {
        const { optimisticData, traceId: listTraceId } = updateData;

        // 更新 traceId（优先使用参数传入的值）
        currentTraceIdRef.current = traceId || listTraceId;

        // 乐观渲染：立即更新 UI
        setCurrentData(optimisticData);
      } else if (traceId) {
        // 如果列表中找不到数据但有 traceId，仍然保存它
        currentTraceIdRef.current = traceId;
      }

      // 同时发起详情接口请求，返回后更新为完整数据
      if (feedType === 'activity') {
        apiGetActivityDetail(id);
      } else {
        apiGetCommunityDetail(id);
      }
    },
    [currentData, list, feedType, apiGetCommunityDetail, apiGetActivityDetail, handleLeave],
  );

  return {
    currentData,
    loading: initialLoading || detailLoading,
    handleThumbnailClick,
    getCurrentId: () => (currentData ? getDetailId(currentData) : undefined),
    handleLeave,
  };
};
